import { useState } from 'react'

import PencilAltIcon from '@heroicons/react/outline/PencilAltIcon'
import PhotographIcon from '@heroicons/react/outline/PhotographIcon'
import ShieldExclamationIcon from '@heroicons/react/outline/ShieldExclamationIcon'
import TrashIcon from '@heroicons/react/outline/TrashIcon'
import UserIcon from '@heroicons/react/outline/UserIcon'
import { useDecision } from '@optimizely/react-sdk'
import { Link } from 'react-router-dom'

import {
  CustomFieldType,
  CustomFieldValidators,
  Member,
  Role,
  RoleType,
} from '@tribeplatform/gql-client/types'
import {
  DefaultMembersStatus,
  useAuthMember,
  useMembers,
  useNetwork,
  useRoles,
  useSpaceUtil,
  useUpdateMember,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { PageHeader } from '@tribeplatform/react-ui-kit/PageHeader'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { FeaturesList } from '../../../lib/optimizely/feature.list'
import { getSeatsCapacity } from '../../../utils/plan'
import { useDebounce } from '../../../utils/useDebounce'
import { AuditLogsModal } from '../../AuditLog/AuditLogsModal'
import { CustomFieldSubtype } from '../../CustomField/utils'
import { Table } from '../../Table'
import { UpgradeBanner } from '../UpgradeBanner'
import { AddAdminModal } from './AddAdminModal'

export const Admins = () => {
  const { generateMemberPath } = useSpaceUtil()
  const [addAdminModal, setAddAdminModal] = useState<boolean>(false)
  const [auditLogsModal, setAuditLogsModal] = useState<string>()
  const [filters, setFilters] = useState({ query: undefined, filterBy: [] })
  const [orderBy, setOrderBy] = useState({
    column: 'createdAt',
    reverse: true,
  })
  const { data: authMember } = useAuthMember()
  const { data: network } = useNetwork()
  const debouncedQuery = useDebounce(filters?.query, 300)
  const { mutateAsync: updateMember } = useUpdateMember()
  const { data: roles } = useRoles()

  const [decisionAuditLogs] = useDecision(FeaturesList.NewModerationPanel)

  const adminRole = roles?.filter(role => role.type === RoleType.ADMIN)?.[0]
  const moderatorRole = roles?.filter(
    role => role.type === RoleType.MODERATOR,
  )?.[0]
  const memberRole = roles?.filter(role => role.type === RoleType.MEMBER)?.[0]

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useMembers({
      fields: {
        profilePicture: 'basic',
        banner: 'basic',
        role: 'basic',
      },
      variables: {
        limit: 15,
        query: debouncedQuery,
        orderBy: orderBy.column,
        reverse: orderBy.reverse,
        roleIds: roles ? [adminRole?.id, moderatorRole?.id] : undefined,
        status: DefaultMembersStatus,
        filterBy: filters?.filterBy || [],
      },
      useInfiniteQueryOptions: { keepPreviousData: true },
    })

  const { nodes: members } = simplifyPaginatedResult<Member>(data)

  const getMemberRole = (member: Member) =>
    members.find(m => m.id === member.id)?.role
  const getOtherMemberRole = (member: Member) =>
    getMemberRole(member)?.id === adminRole?.id ? moderatorRole : adminRole

  const changeRole = async (member: Member, role: Role) => {
    try {
      await updateMember({
        id: member?.id,
        input: {
          roleId: role?.id,
        },
      })
      toast({
        title: 'Member role changed',
        description: `${member?.name}'s role is successfully changed to ${role?.name}.`,
        status: 'success',
      })
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Cannot change the role of this member.',
        status: 'error',
      })
    }
  }

  const columns = [
    {
      name: 'Profile Picture',
      Header: (
        <div className="w-8">
          <PhotographIcon className="w-5 h-5 m-auto" />
        </div>
      ),
      accessor: 'profilePicture.url',
      renderer: 'avatar',
      visible: true,
      width: 'collapse',
    },
    {
      Header: 'ID',
      accessor: 'id',
      renderer: 'memberPrimary',
      visible: false,
      width: 'sm',
    },
    {
      Header: 'Name',
      accessor: 'name',
      renderer: 'memberPrimary',
      visible: true,
      width: 'md',
    },
    {
      Header: 'Username',
      accessor: 'username',
      renderer: 'memberPrimary',
      width: 'md',
      visible: true,
    },
    {
      Header: 'Email',
      accessor: 'email',
      renderer: 'email',
      width: 'lg',
      visible: true,
    },
    {
      Header: 'Role',
      accessor: 'role.name',
      renderer: 'text',
      width: 'sm',
      visible: true,
    },
    {
      Header: 'Tagline',
      accessor: 'tagline',
      width: 'md',
      visible: false,
    },
    {
      Header: 'Created at',
      accessor: 'createdAt',
      renderer: 'date',
      orderKey: 'createdAt',
      width: 'sm',
      visible: false,
      type: CustomFieldType.DATE,
    },
    {
      Header: 'Updated at',
      accessor: 'updatedAt',
      orderKey: 'updatedAt',
      renderer: 'date',
      width: 'sm',
      visible: false,
      type: CustomFieldType.DATE,
    },
    {
      Header: 'Last seen',
      accessor: 'lastSeenAt',
      renderer: 'date',
      format: 'fromNow',
      width: 'sm',
      orderKey: 'lastSeenAt',
      visible: true,
      type: CustomFieldType.DATE,
    },
    {
      Header: 'Email Status',
      accessor: 'status',
      width: 'sm',
      subtype: CustomFieldSubtype.SELECT,
      type: CustomFieldType.TEXT,
      validators: [
        {
          validation: CustomFieldValidators.ENUM,
          value: JSON.stringify(['VERIFIED', 'UNVERIFIED']),
        },
      ],
    },
    {
      Header: 'Locale',
      accessor: 'locale',
      width: 'sm',
    },
    {
      visible: true,
      accessor: 'actions',
      locked: true,
      width: 'xs',
      renderer: member => (
        <Dropdown>
          <Dropdown.ButtonMinimal />
          <Dropdown.Items>
            <Dropdown.Item
              as={Link}
              to={generateMemberPath(member)}
              leadingIcon={<UserIcon />}
            >
              View profile
            </Dropdown.Item>
            {decisionAuditLogs?.enabled && (
              <Dropdown.Item
                leadingIcon={<ShieldExclamationIcon />}
                onClick={() => setAuditLogsModal(member?.id)}
              >
                Audit log
              </Dropdown.Item>
            )}
            <Dropdown.Item
              leadingIcon={<PencilAltIcon />}
              disabled={authMember?.id === member?.id}
              onClick={() => changeRole(member, getOtherMemberRole(member))}
            >
              Change role to {getOtherMemberRole(member)?.name}
            </Dropdown.Item>
            <Dropdown.Item
              leadingIcon={<TrashIcon />}
              disabled={authMember?.id === member?.id}
              onClick={() => changeRole(member, memberRole)}
            >
              Revoke {getMemberRole(member)?.name} role
            </Dropdown.Item>
          </Dropdown.Items>
        </Dropdown>
      ),
    },
  ]

  return (
    <div className="w-full flex flex-col space-y-5">
      <div>
        <PageHeader
          title="Administrators"
          description="Manage roles and permissions for the people who run your community."
        />
        <UpgradeBanner upgradeText="Add seats">
          Your plan supports{' '}
          {getSeatsCapacity(
            network?.seatsCapacity,
            network?.subscriptionPlan?.name,
          )}{' '}
          administrators
        </UpgradeBanner>
      </div>
      <Table
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        columns={columns}
        data={data}
        setFilters={setFilters}
        filters={filters}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        actions={
          <>
            <Button
              size="lg"
              variant="primary"
              onClick={() => setAddAdminModal(true)}
            >
              Add admins
            </Button>
          </>
        }
        title="Admins &amp; moderators"
      />
      {addAdminModal && (
        <AddAdminModal open onClose={() => setAddAdminModal(false)} />
      )}
      {auditLogsModal && (
        <AuditLogsModal
          open
          onClose={() => setAuditLogsModal(null)}
          memberId={auditLogsModal}
        />
      )}
    </div>
  )
}
